import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import KPIMetrics from './KPIMetrics';
import PortfolioAdSpendChart from './PortfolioAdSpendChart';
import PortfolioTable from './PortfolioTable';
import CampaignInsightsTable from '../campaignManager/CampaignInsightsTable';
import { usePortfolioMetrics } from '@/lib/api/hooks/usePortfolioMetrics';
import { usePortfolioCampaigns } from '@/lib/api/hooks/usePortfolioCampaigns';
import type { Campaign } from './demoData';
import type { PortfolioCampaign } from '@/lib/api/services/portfolioCampaignsService';

const PortfolioPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);
  const [selectedPortfolioName, setSelectedPortfolioName] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  
  const { kpiData, rows, isLoading, error } = usePortfolioMetrics();
  
  const { campaigns, totalCount, isLoading: campaignsLoading, error: campaignsError } = 
    usePortfolioCampaigns(
      selectedPortfolioId ? [selectedPortfolioId] : [],
      pageSize,
      pageNumber
    );

  const handlePortfolioClick = (portfolioId: string) => {
    const portfolio = rows.find((r) => r.portfolio_id === portfolioId);
    setSelectedPortfolioId(portfolioId);
    setSelectedPortfolioName(portfolio?.portfolio_name || portfolioId);
    setPageNumber(1);
  };

  const handleBackToPortfolios = () => {
    setSelectedPortfolioId(null);
    setSelectedPortfolioName(null);
    setPageNumber(1);
  };

  const formatCurrency = (value: number) => {
    if (!value) return '₹0';
    if (value >= 1_000_000) return `₹${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `₹${(value / 1_000).toFixed(2)}K`;
    return `₹${value.toFixed(2)}`;
  };

  const formatNumber = (value: number) => {
    if (!value) return '0';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
    return value.toLocaleString();
  };

  const formatPercent = (value: number) => `${(value || 0).toFixed(2)}%`;

  // Convert portfolio campaigns to Campaign format for display
  const mappedCampaigns: Campaign[] = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return [];
    
    return campaigns.map((campaign: PortfolioCampaign) => {
      // Use first campaign_id from array, or campaign_name as fallback
      const campaignId = campaign.campaign_ids && campaign.campaign_ids.length > 0 
        ? campaign.campaign_ids[0] 
        : campaign.campaign_name;
      
      // Determine serving status from status field
      const servingStatus = campaign.status === 'enabled' ? 'Delivering' : 
                           campaign.status === 'paused' ? 'Paused' : 
                           campaign.status || 'Delivering';
      
      // Calculate ROAS if not provided
      const roas = campaign.ad_roas != null 
        ? campaign.ad_roas.toFixed(2)
        : (campaign.spend && campaign.spend > 0 
          ? ((campaign.ad_revenue || 0) / campaign.spend).toFixed(2) 
          : '0');
      
      return {
        id: campaignId,
        name: campaign.campaign_name,
        campaignIds: campaign.campaign_ids,
        isActive: campaign.active === 'Yes' || campaign.status === 'enabled',
        association: campaign.association === 'sponsored products' ? 'associated' : 'not-associated',
        servingStatus: servingStatus,
        targetingType: campaign.targeting_type || 'Manual',
        dailyBudget: campaign.budget ? `₹${campaign.budget}` : '₹0',
        budgetUtilization: campaign.budget_utilization != null 
          ? `${campaign.budget_utilization.toFixed(2)}%` 
          : '0%',
        impressions: formatNumber(campaign.impressions || 0),
        impressionsTrend: 'up',
        clicks: formatNumber(campaign.clicks || 0),
        clicksTrend: 'up',
        sales: formatCurrency(campaign.ad_revenue || 0),
        salesTrend: 'up',
        acos: formatPercent(campaign.ad_acos || 0),
        acosTrend: 'up',
        roas: roas,
        roasTrend: 'up',
        cvr: formatPercent(campaign.cr || 0),
        cvrTrend: 'up',
      };
    });
  }, [campaigns]);

  // Show campaigns if a portfolio is selected
  if (selectedPortfolioId) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToPortfolios}
              className="mr-4"
            >
              Back to Portfolios
            </Button>
            <div className="flex bg-white rounded-lg p-1 shadow-sm">
              <button
                className={`px-6 py-2 rounded-md font-medium transition-colors text-gray-600 hover:text-gray-800`}
                onClick={() => navigate('/dashboard/ads/campaigns')}
              >
                All Campaigns
              </button>
              <button
                className={`px-6 py-2 rounded-md font-medium transition-colors bg-orange-500 text-white`}
                onClick={handleBackToPortfolios}
              >
                All Portfolios
              </button>
            </div>
            <h2 className="text-xl font-semibold ml-4">
              Campaigns in: {selectedPortfolioName}
            </h2>
          </div>
        </div>

        {/* Campaigns Table */}
        <div className="mb-8">
          <CampaignInsightsTable
            data={mappedCampaigns}
            loading={campaignsLoading}
            errorText={campaignsError ? campaignsError.message : null}
            total={totalCount}
            onPageChange={(page, size) => {
              setPageNumber(page);
              setPageSize(size);
            }}
          />
        </div>
      </div>
    );
  }

  // Show portfolios list
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="flex bg-white rounded-lg p-1 shadow-sm">
            <button
              className={`px-6 py-2 rounded-md font-medium transition-colors text-gray-600 hover:text-gray-800`}
              onClick={() => navigate('/dashboard/ads/campaigns')}
            >
              All Campaigns
            </button>
            <button
              className={`px-6 py-2 rounded-md font-medium transition-colors bg-orange-500 text-white`}
            >
              All Portfolios
            </button>
          </div>
        </div>
      </div>
      {/* Portfolio KPIs sourced from /portfolio/metrics */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="text-center text-red-600 mb-8">
          Failed to load portfolio metrics. Please try again later.
        </div>
      ) : (
        <KPIMetrics data={kpiData || []} />
      )}

      {/* Graph similar to campaigns, with different static data */}
      <PortfolioAdSpendChart />

      {/* Table same design as All Campaigns, but driven by portfolio metrics data */}
      <div className="mb-8">
        <PortfolioTable
          rows={rows}
          loading={isLoading}
          errorText={error ? error.message : null}
          onPortfolioClick={handlePortfolioClick}
        />
      </div>
    </div>
  );
};

export default PortfolioPage;
